import { useRef, useState, useEffect } from "react";
import { useInView, animate } from "framer-motion";
import { Reveal, SectionLabel } from "./Reveal";

const STATS = [
  { value: 15, suffix: "+", label: "Cuisines Served" },
  { value: 10000, suffix: "+", label: "Orders Fulfilled" },
  { value: 50, suffix: "+", label: "Team Members" },
  { value: 10000, suffix: "+", label: "Sq Ft of Kitchen Space" },
  { value: 15, suffix: "+", label: "Brands Onboarded" },
];

const MEDIA = [
  { outlet: "NDTV", quote: "What sets Skope apart isn't just their expansion model — it's their comprehensive reimagining of kitchen operations." },
  { outlet: "Business Standard", quote: "By removing the operational barriers to scaling, they're potentially restructuring the entire restaurant industry's growth model." },
  { outlet: "Yahoo Finance", quote: "Aiming to collaborate with 60 varying brands in its inaugural year, Skope is already engaging with well-known restaurant chains." },
  { outlet: "Devdiscourse", quote: "Skope's standout features include AI-driven kitchen management, a unique central tray system, and sustainable operations." },
  { outlet: "Business Wire India", quote: "For an industry where 60% of restaurants traditionally fail within their first year, Skope's model represents a potential paradigm shift." },
  { outlet: "ANI News", quote: "The company has eliminated traditional status barriers, creating an environment where every team member receives equal respect and opportunities." },
];

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 1.8, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val.toLocaleString("en-IN")}{suffix}</span>;
};

const Proof = () => (
  <section id="proof" className="relative bg-[#f6f6f6] py-28 lg:py-40 border-y border-ink/5" data-testid="proof-section">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
      <Reveal><SectionLabel>In Practice — 09</SectionLabel></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 font-display font-semibold text-3xl lg:text-5xl tracking-tight text-ink leading-[1.08]" data-testid="proof-headline">
          PROOF IN OPERATION.
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-2 lg:grid-cols-5 gap-px bg-ink/10 border border-ink/10" data-testid="proof-stats">
        {STATS.map((s) => (
          <div key={s.label} className="bg-white p-6 lg:p-8">
            <div className="font-display font-bold text-3xl lg:text-4xl text-skope">
              <Counter to={s.value} suffix={s.suffix} />
            </div>
            <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-ink/40 uppercase">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-20">
        <Reveal>
          <div className="font-mono text-[11px] tracking-[0.3em] text-ink/40 uppercase">What the media says about Skope Kitchens</div>
        </Reveal>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10" data-testid="proof-media">
          {MEDIA.map((m, i) => (
            <Reveal key={m.outlet} delay={0.05 * i} className="bg-white">
              <figure className="p-8 h-full flex flex-col">
                <blockquote className="text-sm text-ink/55 leading-relaxed flex-1">“{m.quote}”</blockquote>
                <figcaption className="mt-6 font-display font-bold text-ink/80 tracking-wide uppercase text-sm">
                  {m.outlet}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default Proof;
